import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { OPSBELT_APP_URL } from "../lib/env";
import { PageHeader } from "../components/PageHeader";
import { Badge } from "../components/StatusBadge";
import { Table, THead, TH, TBody, TR, TD } from "../components/Table";
import { Loading, ErrorState, EmptyState } from "../components/EmptyState";
import { formatDate, formatBytes } from "../lib/format";
import { OwnerCell } from "../components/OwnerCell";
import { useMe } from "../lib/hooks";

const ACTION_LABEL: Record<string, string> = {
  issued_put: "Issued upload URL",
  issued_get: "Issued download URL",
};

export function AuditPage() {
  const me = useMe();
  const audit = useQuery({
    queryKey: ["audit"],
    queryFn: () => api.audit({ limit: 100 }),
  });

  const events = audit.data?.events;
  // null = full history (paid); a number = the free-tier view window.
  const retentionDays = audit.data?.retentionDays ?? null;
  const isFree = typeof retentionDays === "number";
  const isAdmin =
    me.data?.org.role === "owner" || me.data?.org.role === "admin";

  const [exporting, setExporting] = useState(false);
  async function exportCsv() {
    setExporting(true);
    try {
      const csv = await api.auditExport();
      const url = URL.createObjectURL(
        new Blob([csv], { type: "text/csv;charset=utf-8" }),
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = "blobship-activity.csv";
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  }

  return (
    <div>
      <PageHeader
        title="Activity"
        description="Every upload or download URL minted — by whom, for which object, and from where. Attributed to the person via their key, so it stays accurate even after a key is revoked."
        action={
          isFree ? undefined : (
            <button
              className="btn-secondary"
              onClick={exportCsv}
              disabled={exporting}
            >
              {exporting ? "Exporting…" : "Export CSV"}
            </button>
          )
        }
      />

      {isFree && (
        <div className="mb-5 flex flex-col gap-2 rounded-md border border-surface-200 bg-surface-100 px-4 py-3 text-sm text-ink-muted sm:flex-row sm:items-center sm:justify-between">
          <span>
            Showing the last {retentionDays} days. Full history and CSV export
            are a paid feature
            {isAdmin ? "." : " — ask an organisation admin to upgrade."}
          </span>
          {isAdmin && (
            <a
              href={`${OPSBELT_APP_URL}/billing`}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 font-medium text-accent underline underline-offset-2 hover:no-underline"
            >
              Upgrade in OpsBelt ↗
            </a>
          )}
        </div>
      )}

      {audit.isLoading ? (
        <Loading />
      ) : audit.isError ? (
        <ErrorState error={audit.error} onRetry={audit.refetch} />
      ) : events && events.length > 0 ? (
        <Table>
          <THead>
            <tr>
              <TH>When</TH>
              <TH>By</TH>
              <TH>Action</TH>
              <TH>Object</TH>
              <TH>Bytes</TH>
              <TH>Source IP</TH>
            </tr>
          </THead>
          <TBody>
            {events.map((e) => (
              <TR key={e.id}>
                <TD className="whitespace-nowrap text-ink-muted">
                  {formatDate(e.createdAt)}
                </TD>
                <TD>
                  <OwnerCell email={e.actorEmail} youEmail={me.data?.user.email} />
                </TD>
                <TD>
                  <Badge tone={e.action === "issued_put" ? "ok" : "neutral"}>
                    {ACTION_LABEL[e.action] ?? e.action}
                  </Badge>
                </TD>
                <TD className="mono text-xs text-ink-muted">
                  {e.objectKey ?? "—"}
                </TD>
                <TD className="text-ink-muted">{formatBytes(e.bytes)}</TD>
                <TD className="mono text-xs text-ink-muted">
                  {e.sourceIp ?? "—"}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      ) : (
        <EmptyState
          title="No events yet"
          description="Authorization events appear here as soon as keys are used."
        />
      )}
    </div>
  );
}
